import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetLynFormPropertyMutation } from 'store/features/process/processApi';

dayjs.extend(utc);
export const utcConverter = (dayjsDate, day = false) => {
	if (day) {
		// Extract year and month, set day to 1
		const year = dayjsDate?.year();
		const month = dayjsDate?.month(); // dayjs months are 0-indexed
		return dayjs?.utc().year(year).month(month).date(1).startOf('day');
	}

	// Original functionality - extract the date part as a string in YYYY-MM-DD format
	const dateStr = dayjsDate?.format('YYYY-MM-DD');
	return dayjs?.utc(dateStr, 'YYYY-MM-DD');
};
export const hasGlKey = (data) => {
	if (!data || typeof data !== 'object') {
		return false;
	}
	return Object.keys(data).some((key) => key.startsWith('gl_'));
};

export const useControlData = (processCode) => {
	const userId = useSelector((state) => state.profile.profileData.userId);
	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

	const [GetLynFormProperty] = useGetLynFormPropertyMutation();
	const [data, setData] = useState({
		rightOfUseAssetValue: [],
		glleaseLiability: [],
		costCenterInterestExpense: [],
		glInterestExpense: [],
		leaseExpenseCostCenter: [],
		leaseExpense: [],
		leasePayableControlAccount: [],
		glAdvanceRent: [],
		depreciationRoUCostCenter: [],
		depreciationRoU: [],
		accumDepreciationRoU: [],
		glBankAccount: [],
		securityDepositHead: [],
		accountsPayableHead: [],
	});

	useEffect(() => {
		const fetchLynFormProperty = (code) =>
			GetLynFormProperty({
				type: 'FORM',
				fieldCode: processCode,
				propertyCode: code,
				companyCode,
				status: 'Active',
			});

		Promise.all([
			fetchLynFormProperty('PROPERTY_rightOfUseAssetValue'),
			fetchLynFormProperty('PROPERTY_glleaseLiability'),
			fetchLynFormProperty('PROPERTY_costCenterInterestExpense'),
			fetchLynFormProperty('PROPERTY_glInterestExpense'),
			fetchLynFormProperty('PROPERTY_leaseExpenseCostCenter'),
			fetchLynFormProperty('PROPERTY_leaseExpense'),
			fetchLynFormProperty('PROPERTY_leasePayableControlAccount'),
			fetchLynFormProperty('PROPERTY_glAdvanceRent'),
			fetchLynFormProperty('PROPERTY_depreciationRoUCostCenter'),
			fetchLynFormProperty('PROPERTY_depreciationRoU'),
			fetchLynFormProperty('PROPERTY_accumDepreciationRoU'),
			fetchLynFormProperty('PROPERTY_glBankAccount'),
			fetchLynFormProperty('PROPERTY_securityDepositHead'),
			fetchLynFormProperty('PROPERTY_accountsPayableHead'),
		])
			.then(([res1, res2, res3, res4, res5, res6, res7, res8, res9, res10, res11, res12, res13, res14]) => {
				setData({
					rightOfUseAssetValue: res1?.data?.lynProperties,
					glleaseLiability: res2?.data?.lynProperties,
					costCenterInterestExpense: res3?.data?.lynProperties,
					glInterestExpense: res4?.data?.lynProperties,
					leaseExpenseCostCenter: res5?.data?.lynProperties,
					leaseExpense: res6?.data?.lynProperties,
					leasePayableControlAccount: res7?.data?.lynProperties,
					glAdvanceRent: res8?.data?.lynProperties,
					depreciationRoUCostCenter: res9?.data?.lynProperties,
					depreciationRoU: res10?.data?.lynProperties,
					accumDepreciationRoU: res11?.data?.lynProperties,
					glBankAccount: res12?.data?.lynProperties,
					securityDepositHead: res13?.data?.lynProperties,
					accountsPayableHead: res14?.data?.lynProperties,
				});
			})
			.catch((err) => {
				console.error('Error fetching data:', err);
			});
	}, [userId]);

	return { lynFormProperties: data };
};
export const formatNumber = (value, decimalPlaces = 2) => {
	// First check if value is null or undefined
	if (value === null || value === undefined) {
		return '0.00';
	}

	// Handle various input types
	const num = typeof value === 'string' ? parseFloat(value) : Number(value);

	// Check if the result is a valid number
	if (isNaN(num)) {
		return '0.00';
	}

	return num.toLocaleString('en-US', {
		minimumFractionDigits: decimalPlaces,
		maximumFractionDigits: decimalPlaces,
	});
};
export const processArrays = (grossRents, advanceAmounts) => {
	// Store original lengths
	const originalGrossRentsLength = grossRents.length;
	const originalAdvanceAmountsLength = advanceAmounts.length;

	// Calculate effective length
	const effectiveLength = Math.max(originalGrossRentsLength, originalAdvanceAmountsLength);

	// Create copies of the original arrays
	const paddedGrossRents = [...grossRents];
	const paddedAdvanceAmounts = [...advanceAmounts];

	// Pad the shorter array with zeros
	if (originalGrossRentsLength < effectiveLength) {
		paddedGrossRents.push(...Array(effectiveLength - originalGrossRentsLength).fill(0));
	}

	if (originalAdvanceAmountsLength < effectiveLength) {
		paddedAdvanceAmounts.push(...Array(effectiveLength - originalAdvanceAmountsLength).fill(0));
	}

	// Return the results
	return {
		processedGrossRents: paddedGrossRents,
		processedAdvanceAmounts: paddedAdvanceAmounts,
		effectiveLength: effectiveLength,
		effectiveAdvanceDuration: originalAdvanceAmountsLength,
	};
};
// Advanced reusable setter function that can handle nested paths
// Create a reusable state updater factory
export const stateUpdater = (setStateFn) => {
	// Return a path-based updater function
	return (path, value) => {
		setStateFn((prev) => {
			// If path is a string, convert it to array (e.g., 'modalData.head' -> ['modalData', 'head'])
			const pathArray = typeof path === 'string' ? path.split('.') : path;

			// For simple case - updating the entire state object
			if (pathArray.length === 0) return value;

			// Create a copy of the previous state
			const result = { ...(typeof prev === 'object' ? prev : {}) };

			// Navigate to the right location in the state tree
			let current = result;
			for (let i = 0; i < pathArray.length - 1; i++) {
				const key = pathArray[i];
				// Ensure the path exists and is an object
				current[key] = current[key] && typeof current[key] === 'object' ? { ...current[key] } : {};
				current = current[key];
			}

			// Set the value at the final location
			const finalKey = pathArray[pathArray.length - 1];

			// Handle merging if the value is an object and the target is also an object
			if (typeof value === 'object' && value !== null && typeof current[finalKey] === 'object' && current[finalKey] !== null) {
				current[finalKey] = { ...current[finalKey], ...value };
			} else {
				current[finalKey] = value;
			}

			return result;
		});
	};
};

//Documentation: [stateUpdater] :
/*
const [formState, setFormState] = useState({
  showFormSubmitModal: false,
  submitPayload: null,
  modalData: {
    head: state?.processName ?? '',
    label: 'Do you want to proceed?'
  }
});
const updateFormState = createStateUpdater(setFormState);

Usage for form state
updateFormState('showFormSubmitModal', true);
updateFormState('modalData.head', 'New Process');

You can use it with any other state in your component
const [userPreferences, setUserPreferences] = useState({ theme: 'light', fontSize: 'medium' });
const updateUserPreferences = createStateUpdater(setUserPreferences);

Usage for user preferences
updateUserPreferences('theme', 'dark');
updateUserPreferences('notifications.email', true);
*/

export const isSequential = (objects) => {
	// Handle empty array or null/undefined input
	if (!objects || objects.length === 0) {
		return true;
	}

	for (let i = 0; i < objects.length; i++) {
		const current = objects[i];
		const from = parseInt(current.from);
		const to = parseInt(current.to);

		// Check if from <= to within the same object
		if (from > to) {
			return false;
		}

		// For objects after the first one, check if from is sequential
		if (i > 0) {
			const previous = objects[i - 1];
			const previousTo = parseInt(previous.to);

			// Current 'from' should be exactly previousTo + 1
			if (from !== previousTo + 1) {
				return false;
			}
		}
	}
	return true;
};

export const isGrossRentLessThanAdvanceOnEachItem = (grossRent, advanceAmount) => {
	// Check if arrays have the same length
	if (grossRent.length !== advanceAmount.length) {
		throw new Error('Arrays must have the same length');
	}

	// Check if every element in grossRent is less than corresponding element in advanceAmount
	return grossRent.every((element, index) => element >= advanceAmount[index]);
};

export const transformToGrossRentListSet = (inputData) => {
	const grossRentListSet = [];

	// Helper function to format amount with commas
	function formatAmount(amount) {
		return amount.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	}

	// Iterate through each date key in the input object
	Object.keys(inputData).forEach((dateKey) => {
		const leaseData = inputData[dateKey];
		const formattedGroup = [];

		// Transform each lease period into the required format
		leaseData.forEach((item) => {
			const formattedItem = {
				from: item['Rent (From Month)'],
				to: item['Rent (To Month)'].toString(),
				amount: formatAmount(item['Rent Amount']),
				remarks: '', // empty string by default
			};

			formattedGroup.push(formattedItem);
		});

		// Add the formatted group to the grossRentListSet
		if (formattedGroup.length > 0) {
			grossRentListSet.push(formattedGroup);
		}
	});

	return grossRentListSet;
};

// Helper function to convert camelCase to Title Case for dropdown labels
export const toTitleCase = (text) => {
	if (!text) return '';
	if (text === 'payment') return 'Lease Payment';
	const result = text.replace(/([A-Z])/g, ' $1');
	return result.charAt(0).toUpperCase() + result.slice(1);
};
